import React, {useState} from 'react';

import {
  Anchor,
  Banner,
  Heading,
  Icon,
  SegmentedControl,
  Text,
  WithTooltip
} from '@albert-io/atomic';
import EmptyState from 'client/Classrooms/Classroom/Teacher/EmptyState/index.react';
import RestrictFreePracticeModal from 'client/Classrooms/Classroom/Teacher/Subjects/RestrictFreePracticeModal.react';
import {SubjectsListCard} from 'client/Classrooms/Classroom/Teacher/Subjects/SubjectsListCard.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {SubjectsFilterMenu} from './SubjectsFilterMenu';
import {useClassSubjects} from './useClassSubjects';

interface Props {
  classroomId: string;
}

type SubjectsTab = 'all' | 'independent';

// (i) tooltip copy carried over verbatim from the legacy TeacherSubjectsList heading tooltips.
const ALL_SUBJECTS_TOOLTIP = <p className='mb-x0!'>Subjects practiced by your students.</p>;
const INDEPENDENT_PRACTICE_TOOLTIP = (
  <p className='mb-x0!'>
    If a subject has open access, that means students can independently practice and view solutions
    for any question in that subject&apos;s Practice tab. Otherwise, students can only practice and
    view solutions from questions that you&apos;ve assigned to them.
    <br />
    <br />
    Close access by selecting a subject below. Open access to more subjects from our Subjects
    library.
  </p>
);

const TabInfo = ({content}: {content: React.ReactNode}) => (
  <WithTooltip as='span' content={content} placement='top' theme='light'>
    <Icon color='tertiary' icon='info-circle' iconStyle='regular' size='inherit' />
  </WithTooltip>
);

// v2 per-class Subjects tab (Figma 4746-78638): the colored subject-card grid with per-card
// Open/Close access + partial credit (the legacy SubjectsListCard) and a Filters popover for hidden
// subjects. Flux-free port of TeacherSubjectsList. Licensed teachers at licensed schools get the
// All Subjects / Independent Practice tabs (gated on canManageAccess, mirroring the legacy menu).
export const ClassSubjects = ({classroomId}: Props) => {
  const classSubjects = useClassSubjects(classroomId);
  const [tab, setTab] = useState<SubjectsTab>('all');

  if (!classSubjects.isReady) {
    return <LoadingIndicator />;
  }

  const {classroom} = classSubjects;
  if (classSubjects.hasNoSubjects || !classroom) {
    return (
      <EmptyState
        title='No class subjects'
        body='No subjects are currently available for students to practice in this class. Visit the library to assign or open student practice.'
      />
    );
  }

  const showTabs = classSubjects.canManageAccess;
  const isIndependentTab = showTabs && tab === 'independent';
  const visibleSubjects = isIndependentTab
    ? classSubjects.subjects.filter(
        (subject) => !subject.getClassroomRelationships(classroomId).getRestrictFreePractice()
      )
    : classSubjects.subjects;

  return (
    <div className='flex flex-col gap-x3 pt-x4'>
      <Heading as='h2' className='m-x0' size='s'>
        Subjects
      </Heading>
      {(showTabs || classSubjects.hasHiddenSubjects) && (
        <div className='flex flex-wrap items-center justify-between gap-x2'>
          {showTabs ? (
            <SegmentedControl
              buttonClassName='font-normal'
              selectedIdx={isIndependentTab ? 1 : 0}
              onSelect={(idx) => setTab(idx === 1 ? 'independent' : 'all')}
              options={[
                <span key='all' className='flex items-center gap-x1'>
                  All Subjects
                  <TabInfo content={ALL_SUBJECTS_TOOLTIP} />
                </span>,
                <span key='independent' className='flex items-center gap-x1'>
                  Independent Practice
                  <TabInfo content={INDEPENDENT_PRACTICE_TOOLTIP} />
                </span>
              ]}
            />
          ) : (
            <span />
          )}
          {classSubjects.hasHiddenSubjects && (
            <SubjectsFilterMenu
              showHidden={classSubjects.showHidden}
              onToggle={classSubjects.setShowHidden}
            />
          )}
        </div>
      )}
      {isIndependentTab && visibleSubjects.length === 0 ? (
        <div className='flex flex-col items-center py-x6 text-center'>
          <Text color='secondary'>
            Students can only practice on questions you&apos;ve assigned to them. If you want to
            allow students to answer any question in a subject,{' '}
            <Anchor to='/subjects/high-school/test-prep'>visit the Library tab</Anchor> and toggle
            on access.
          </Text>
        </div>
      ) : (
        <>
          {isIndependentTab && (
            <Banner>
              <Banner.Icon icon='info' iconStyle='solid' />
              <span>
                Open access to more subjects from the{' '}
                <Anchor to='/subjects/high-school/test-prep'>Library</Anchor>
              </span>
            </Banner>
          )}
          <div className='grid grid-cols-1 gap-x3 s:grid-cols-2 m:grid-cols-3'>
            {visibleSubjects.map((subject) => (
              <SubjectsListCard
                key={subject.getId()}
                classroom={classroom}
                subject={subject}
                isAdaptive={subject.isAdaptive()}
                isHidden={subject.isHidden()}
                showActions={subject.isAdaptive() || classSubjects.canManageAccess}
                handleCloseAccess={classSubjects.requestCloseAccess}
                handleOpenAccess={classSubjects.openAccess}
                handlePartialCreditToggled={classSubjects.togglePartialCredit}
              />
            ))}
          </div>
        </>
      )}
      {classSubjects.pendingClose && (
        <RestrictFreePracticeModal
          fromClassroom
          handleClose={classSubjects.cancelCloseAccess}
          onRestrict={classSubjects.confirmCloseAccess}
        />
      )}
    </div>
  );
};
